/**
 * Chat launch helpers for dashboard empty-state flows and CompliMind Insight follow-up.
 *
 * Same approach as PAM / group hub: open `/chat?q=...` (optionally with
 * resource_id / resource_type / resource_title). ChatLayout already auto-sends
 * that query; nothing in ChatMessageView or ChatSearchInput needs to know
 * about dashboards.
 *
 * 1. Brainstorm — user has just found dashboards and does not know what to track.
 * 2. Populate — user has an empty (or existing) dashboard and needs help filling it.
 * 3. Insight report — user wants a deeper analysis of the current dashboard/group.
 *
 * Entry points expose several labelled examples (same pattern as Groups overview
 * follow-ups) rather than a single CTA.
 */

import { toTenantPath } from 'src/lib/tenantRouting';

export type DashboardChatScope = 'dashboard' | 'group';
export type DashboardChatIntent = 'brainstorm' | 'populate_empty' | 'populate_existing' | 'insight_report';

export type DashboardChatExample = {
  id: string;
  label: string;
  query: string;
};

export function dashboardChatQuery(intent: DashboardChatIntent, scope: DashboardChatScope = 'dashboard'): string {
  if (intent === 'brainstorm') {
    return (
      "I've just started using assurance dashboards and I'm not sure what I should track. " +
      'Please help me brainstorm. Ask about my role, the sites or services I look after, ' +
      'and what good assurance looks like for me. Then propose a dashboard structure I can approve, ' +
      'including assurance thresholds on every metric card in that same proposal. ' +
      'Do not ask me to choose thresholds afterwards. ' +
      'If I upload a spreadsheet or paste a table, use that too.'
    );
  }
  const place = scope === 'group' ? 'group' : 'dashboard';
  if (intent === 'insight_report') {
    return (
      `I've read the CompliMind Insight summary for this ${place}. ` +
      'Please produce a more in-depth report: examine the child metrics and historical trends, ' +
      'explain what is driving the current assurance level, call out specific areas of concern with evidence, ' +
      `and recommend concrete next actions I can take on this ${place}.`
    );
  }
  if (intent === 'populate_empty') {
    return (
      `This ${place} is empty and I'm not sure where to start. Please help me populate it. ` +
      `This ${place} already exists and is the parent — attach groups and cards directly under it. ` +
      'Do not add one extra group and put everything under that. ' +
      'Ask what I should track here, or use a spreadsheet if I upload one, ' +
      `then propose those direct children that I can approve.`
    );
  }
  return (
    `Please help me add to or populate this ${place}. ` +
    'Ask what I want to add, or use a spreadsheet if I upload one.'
  );
}

export function dashboardHomeChatExamples(): DashboardChatExample[] {
  return [
    {
      id: 'brainstorm',
      label: 'Brainstorm what to track',
      query: dashboardChatQuery('brainstorm')
    },
    {
      id: 'upload_spreadsheet',
      label: 'Upload a spreadsheet',
      query:
        'I have a spreadsheet or table of metrics. Help me turn it into an assurance dashboard. ' +
        'Ask me to upload it or paste the table, then propose a dashboard structure I can approve, ' +
        'including assurance thresholds on every metric card in that same proposal. ' +
        'Do not ask me to choose thresholds afterwards.'
    },
    {
      id: 'suggest_for_role',
      label: 'Suggest dashboards for my role',
      query:
        'Based on my role and the sites or services I look after, suggest 2–3 assurance dashboards I should have, ' +
        'with the groups and cards in each. Ask me anything you need first. ' +
        'Include assurance thresholds on every metric card in that same proposal. ' +
        'Do not ask me to choose thresholds afterwards.'
    }
  ];
}

export function dashboardPopulateChatExamples(scope: DashboardChatScope = 'dashboard'): DashboardChatExample[] {
  const place = scope === 'group' ? 'group' : 'dashboard';
  return [
    {
      id: 'populate',
      label: 'Help me populate this',
      query: dashboardChatQuery('populate_empty', scope)
    },
    {
      id: 'upload_spreadsheet',
      label: 'Upload a spreadsheet',
      query:
        `I have a spreadsheet or table of metrics for this ${place}. Help me populate it from that. ` +
        `This ${place} already exists — attach groups and cards directly under it, ` +
        'not under one extra wrapper group. ' +
        'Ask me to upload it or paste the table, then propose those direct children I can approve.'
    },
    {
      id: 'suggest_cards',
      label: 'Suggest groups and cards',
      query:
        `Suggest groups and cards to add to this ${place} based on what it is for. ` +
        `This ${place} already exists — attach them directly under it, not under one extra wrapper group. ` +
        `Ask me what this ${place} should track if you need to, then propose those direct children I can approve.`
    }
  ];
}

export function dashboardInsightChatExamples(scope: DashboardChatScope = 'dashboard'): DashboardChatExample[] {
  const place = scope === 'group' ? 'group' : 'dashboard';
  return [
    {
      id: 'insight_report',
      label: 'Get a more in-depth report',
      query: dashboardChatQuery('insight_report', scope)
    },
    {
      id: 'why_changed',
      label: 'Why did assurance change',
      query:
        `Based on this ${place}, explain what drove the latest assurance change. ` +
        'Point to specific child metrics and trends.'
    },
    {
      id: 'focus',
      label: 'What should I focus on',
      query: `Based on this ${place}, what should I focus on this week, and what actions should I take?`
    }
  ];
}

export const DASHBOARD_CHAT_RETURN_KEY = 'dashboard-chat-return-to';

/** Keep only an in-app path. Query values like `https://` or `//host` are dropped. */
export function dashboardReturnPath(value: string | null | undefined): string | null {
  if (!value) return null;
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('\\') || decoded.includes('://')) {
    return null;
  }
  return value;
}

export function buildDashboardChatPath(opts: {
  query: string;
  resourceId?: string;
  resourceTitle?: string;
  returnTo?: string;
}): string {
  const params = new URLSearchParams({ q: opts.query });
  if (opts.resourceId) {
    params.set('resource_id', opts.resourceId);
    params.set('resource_type', 'dashboard');
    params.set('resource_title', opts.resourceTitle ?? opts.resourceId);
  }
  if (opts.returnTo) {
    params.set('return_to', opts.returnTo);
  }
  return `/chat?${params.toString()}`;
}

/** Open chat in a new tab, same as PAM question chat and group-hub follow-ups. */
export function openDashboardChat(opts: {
  query: string;
  tenantCode: string | null;
  resourceId?: string;
  resourceTitle?: string;
  returnTo?: string;
}): void {
  window.open(toTenantPath(buildDashboardChatPath(opts), opts.tenantCode), '_blank');
}
